import { useState, useEffect, useCallback } from 'react';
import {
  Crosshair, Send, Mail, Users, Eye, Play, CheckCircle2,
  XCircle, Clock, Filter, RefreshCw, AlertCircle, Reply, Inbox
} from 'lucide-react';

const API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';

function getToken(): string {
  try { return JSON.parse(localStorage.getItem('acq_auth') || '{}').token || ''; }
  catch { return ''; }
}
function authHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` };
}

interface WarmupAccount {
  id: string; email: string; provider: string; status: string;
  warmup_score: number; last_error: string | null; created_at: string;
}

interface Lead {
  id: string; name: string; email: string; firm: string;
  specialty: string; city: string; status: string; country: string;
}

interface SniperAssignment {
  outlookAccount: WarmupAccount;
  lead: Lead;
  status: 'pending' | 'sent' | 'failed';
  sentAt?: string;
}

interface ReplyEmail {
  id: string; direction: 'inbound' | 'outbound'; fromEmail: string;
  toEmail: string; subject: string; bodyText: string; bodyHtml?: string;
  receivedAt: string; isRead: boolean; leadId?: string;
}

const SPECIALTIES = [
  'All', 'Direito Trabalhista', 'Direito Tributário', 'Direito Civil',
  'Direito Empresarial', 'Propriedade Intelectual', 'LGPD/Compliance',
  'Direito Penal', 'Direito Ambiental', 'Other'
];

const card: React.CSSProperties = {
  background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
  borderRadius: 12, padding: 20,
};

const smallBtn = (bg: string, color: string): React.CSSProperties => ({
  background: `${bg}18`, color, border: `1px solid ${bg}44`,
  borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600,
});

export function SniperPage() {
  const [outlookAccounts, setOutlookAccounts] = useState<WarmupAccount[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [assignments, setAssignments] = useState<SniperAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [tab, setTab] = useState<'assignments' | 'preview' | 'replies'>('assignments');
  const [replies, setReplies] = useState<ReplyEmail[]>([]);
  const [selectedReply, setSelectedReply] = useState<ReplyEmail | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);

  // Fetch unused outlook accounts from warmup pool + leads
  const fetchData = useCallback(async () => {
    try {
      const [accRes, leadsRes, inboxRes] = await Promise.all([
        fetch(`${API}/warmup/accounts`, { headers: authHeaders() }),
        fetch(`${API}/campaigns/leads?country=BR&limit=1000`, { headers: authHeaders() }).catch(() => null),
        fetch(`${API}/emails/all`, { headers: authHeaders() }).catch(() => null),
      ]);
      const accData = await accRes.json();
      const allAccounts: WarmupAccount[] = accData.accounts || [];
      const unusedAccounts = allAccounts.filter(a => a.warmup_score < 10 || a.status !== 'active');
      setOutlookAccounts(unusedAccounts.length > 0 ? unusedAccounts : allAccounts);

      if (leadsRes) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData.leads || []);
      }

      // Load inbox emails and filter to only show replies from non-warmup sources
      if (inboxRes) {
        const inboxData = await inboxRes.json();
        const allEmails: ReplyEmail[] = inboxData.emails || [];
        // Get set of all warmup outlook emails for filtering
        const warmupEmails = new Set(allAccounts.map(a => a.email.toLowerCase()));
        // Show inbound emails that are NOT from warmup accounts (i.e., real lawyer replies)
        const lawyerReplies = allEmails.filter(e =>
          e.direction === 'inbound' &&
          !warmupEmails.has(e.fromEmail.toLowerCase()) &&
          !e.fromEmail.toLowerCase().includes('mailer-daemon') &&
          !e.subject.toLowerCase().includes('undelivered')
        );
        setReplies(lawyerReplies);
      }
    } catch (err) {
      console.error('[Sniper] fetch error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-assign: pair each outlook account with a lead (1:1)
  useEffect(() => {
    if (outlookAccounts.length === 0 || leads.length === 0) return;
    const filtered = filter === 'All' ? leads : leads.filter(l => l.specialty === filter);
    const pairs: SniperAssignment[] = [];
    const limit = Math.min(outlookAccounts.length, filtered.length);
    for (let i = 0; i < limit; i++) {
      pairs.push({ outlookAccount: outlookAccounts[i], lead: filtered[i], status: 'pending' });
    }
    setAssignments(pairs);
  }, [outlookAccounts, leads, filter]);

  // Generate email preview for a specific assignment
  function generateEmailPreview(assignment: SniperAssignment): string {
    const { lead } = assignment;
    const specialty = lead.specialty || 'questões jurídicas';
    const firmName = lead.firm || 'seu escritório';
    return `Prezado(a) Dr(a). ${lead.name},

Meu nome é Leo, e trabalho com uma empresa de tecnologia que está expandindo suas operações no Brasil. Estamos enfrentando algumas questões relacionadas a ${specialty} e gostaríamos de contar com a expertise de ${firmName}.

Vi que o(a) senhor(a) tem ampla experiência nessa área. Gostaria de saber se seria possível uma breve conversa para entender melhor como podemos proceder.

Agradeço desde já pela atenção.

Atenciosamente,
Leo`;
  }

  // Handle sending (mock for now — will connect to backend API)
  async function handleStartCampaign() {
    if (assignments.length === 0) return;
    setSending(true);
    let sent = 0, failed = 0;

    for (let i = 0; i < assignments.length; i++) {
      const a = assignments[i];
      try {
        const res = await fetch(`${API}/sniper/send`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({
            outlookEmail: a.outlookAccount.email,
            outlookId: a.outlookAccount.id,
            leadEmail: a.lead.email,
            leadName: a.lead.name,
            subject: `Consulta sobre ${a.lead.specialty || 'questões jurídicas'} — ${a.lead.city || 'Brasil'}`,
            body: generateEmailPreview(a),
          }),
        });
        if (res.ok) {
          sent++;
          setAssignments(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'sent', sentAt: new Date().toISOString() } : p));
        } else {
          failed++;
          setAssignments(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'failed' } : p));
        }
      } catch {
        failed++;
        setAssignments(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'failed' } : p));
      }
      setSentCount(sent);
      setFailedCount(failed);
      // Random delay between sends (30s - 180s in production, 1s for preview)
      await new Promise(r => setTimeout(r, 1000));
    }
    setSending(false);
  }

  const filteredAssignments = assignments.filter(a => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return a.lead.name.toLowerCase().includes(q) || a.lead.email.toLowerCase().includes(q) || a.lead.firm?.toLowerCase().includes(q);
    }
    return true;
  });

  const pendingCount = assignments.filter(a => a.status === 'pending').length;
  const sentTotal = assignments.filter(a => a.status === 'sent').length;
  const failedTotal = assignments.filter(a => a.status === 'failed').length;

  if (loading) return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>Loading Sniper Campaign...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'inline-flex', padding: 6, borderRadius: 10, background: 'linear-gradient(135deg, #059669, #10b981)' }}>
              <Crosshair size={20} color="#fff" />
            </span>
            Brazil Sniper Campaign
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-dim)' }}>
            1 Outlook account → 1 Lawyer · Pure text · No links · Consulting approach
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchData} style={{ ...smallBtn('#6366f1', '#a5b4fc'), padding: '6px 12px' }}>
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={handleStartCampaign}
            disabled={sending || pendingCount === 0}
            className="btn btn-primary"
            style={{ fontSize: 12, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 5, opacity: sending ? 0.6 : 1 }}
          >
            {sending ? <><RefreshCw size={12} className="spin" /> Sending {sentCount}/{assignments.length}...</> : <><Play size={12} /> Start Campaign ({pendingCount})</>}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Available Accounts', value: outlookAccounts.length, Icon: Mail, color: '#a5b4fc' },
          { label: 'Target Lawyers', value: leads.length, Icon: Users, color: '#6ee7b7' },
          { label: 'Assigned Pairs', value: assignments.length, Icon: Crosshair, color: '#c4b5fd' },
          { label: 'Sent', value: sentTotal, Icon: Send, color: '#22c55e' },
          { label: 'Failed', value: failedTotal, Icon: XCircle, color: failedTotal > 0 ? '#f87171' : '#94a3b8' },
        ].map((s, i) => (
          <div key={i} style={{ ...card, padding: '14px 12px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
              <span style={{ display: 'inline-flex', padding: 6, borderRadius: 8, background: `${s.color}15` }}>
                <s.Icon size={16} color={s.color} />
              </span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color, margin: '2px 0' }}>{s.value}</div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Filter size={14} color="var(--text-dim)" />
        {SPECIALTIES.map(s => (
          <button
            key={s} onClick={() => setFilter(s)}
            style={{
              padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              border: filter === s ? '1px solid #6366f1' : '1px solid var(--border-default)',
              background: filter === s ? '#312e81' : 'transparent',
              color: filter === s ? '#a5b4fc' : 'var(--text-dim)',
              transition: 'all 0.15s',
            }}
          >{s}</button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <input
            type="text" placeholder="Search lawyer..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              padding: '6px 12px', fontSize: 12, background: 'var(--bg-input)',
              color: 'var(--text-primary)', border: '1px solid var(--border-default)',
              borderRadius: 6, outline: 'none', width: 200,
            }}
          />
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border-default)' }}>
        {([
          { key: 'assignments' as const, Icon: Crosshair, label: `Assignments (${filteredAssignments.length})` },
          { key: 'preview' as const, Icon: Eye, label: 'Email Preview' },
          { key: 'replies' as const, Icon: Inbox, label: `Replies (${replies.length})` },
        ]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            background: 'transparent', border: 'none',
            color: tab === t.key ? (t.key === 'replies' && replies.length > 0 ? '#22c55e' : '#a5b4fc') : 'var(--text-dim)',
            borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent',
            transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Assignments Tab */}
      {tab === 'assignments' && (
        <div style={{ ...card }}>
          {filteredAssignments.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>
              <Users size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
              <p>No assignments yet. Import Outlook accounts in Warmup and discover leads in Campaigns first.</p>
            </div>
          ) : (
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>#</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Outlook Account</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>→</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Lawyer</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Firm</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Specialty</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssignments.map((a, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '8px 10px', color: 'var(--text-dim)' }}>{i + 1}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--text-secondary)', fontSize: 11, fontFamily: 'monospace' }}>
                        {a.outlookAccount.email.substring(0, 20)}...
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--text-dim)' }}>→</td>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{a.lead.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{a.lead.email}</div>
                      </td>
                      <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{a.lead.firm || '-'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: '#6366f122', color: '#a5b4fc',
                        }}>{a.lead.specialty || 'General'}</span>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: a.status === 'sent' ? '#22c55e22' : a.status === 'failed' ? '#f8717122' : '#fbbf2422',
                          color: a.status === 'sent' ? '#22c55e' : a.status === 'failed' ? '#f87171' : '#fbbf24',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                          {a.status === 'sent' ? <><CheckCircle2 size={10} /> Sent</> :
                           a.status === 'failed' ? <><XCircle size={10} /> Failed</> :
                           <><Clock size={10} /> Pending</>}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                        <button onClick={() => { setPreviewIdx(i); setTab('preview'); }}
                          style={{ ...smallBtn('#6366f1', '#a5b4fc') }}>
                          <Eye size={10} /> Preview
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Preview Tab */}
      {tab === 'preview' && (
        <div style={{ ...card }}>
          {assignments.length === 0 ? (
            <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 30 }}>No assignments to preview.</p>
          ) : (() => {
            const idx = previewIdx !== null ? previewIdx : 0;
            const a = assignments[idx];
            if (!a) return null;
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 15, color: 'var(--text-primary)' }}>
                    Email Preview — #{idx + 1} of {assignments.length}
                  </h3>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setPreviewIdx(Math.max(0, idx - 1))} disabled={idx === 0}
                      style={{ ...smallBtn('#6366f1', '#a5b4fc'), opacity: idx === 0 ? 0.4 : 1 }}>← Prev</button>
                    <button onClick={() => setPreviewIdx(Math.min(assignments.length - 1, idx + 1))} disabled={idx >= assignments.length - 1}
                      style={{ ...smallBtn('#6366f1', '#a5b4fc'), opacity: idx >= assignments.length - 1 ? 0.4 : 1 }}>Next →</button>
                  </div>
                </div>
                <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border-default)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>
                    <strong>From:</strong> {a.outlookAccount.email}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>
                    <strong>To:</strong> {a.lead.name} &lt;{a.lead.email}&gt;
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>
                    <strong>Subject:</strong> Consulta sobre {a.lead.specialty || 'questões jurídicas'} — {a.lead.city || 'Brasil'}
                  </div>
                  <div style={{
                    borderTop: '1px solid var(--border-default)', paddingTop: 12,
                    whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.7,
                  }}>
                    {generateEmailPreview(a)}
                  </div>
                </div>
                <div style={{ padding: '10px 14px', background: 'rgba(99,102,241,0.06)', borderRadius: 8, fontSize: 11, color: 'var(--text-dim)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <AlertCircle size={14} color="#a5b4fc" style={{ flexShrink: 0, marginTop: 2 }} />
                  <span><strong style={{ color: '#a5b4fc' }}>Note:</strong> In production, each email will be AI-generated with unique content based on the lawyer's specialty, firm, and recent activity. No links, no HTML, pure text.</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Replies Tab */}
      {tab === 'replies' && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '360px 1fr',
            height: 500,
          }}>
            {/* Left: reply list */}
            <div style={{ borderRight: '1px solid var(--border-default)', overflowY: 'auto', background: 'var(--bg-surface)' }}>
              {replies.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>
                  <Inbox size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
                  <p style={{ fontSize: 13 }}>No lawyer replies yet</p>
                  <p style={{ fontSize: 11, marginTop: 4 }}>Replies from lawyers will appear here after sending your campaign.</p>
                </div>
              ) : (
                replies.map(r => (
                  <div
                    key={r.id}
                    onClick={() => setSelectedReply(r)}
                    style={{
                      padding: '12px 14px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border-default)',
                      background: selectedReply?.id === r.id ? 'var(--accent-bg)' : 'transparent',
                      borderLeft: selectedReply?.id === r.id ? '3px solid var(--accent)' : !r.isRead ? '3px solid #22c55e' : '3px solid transparent',
                      transition: 'background 0.1s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontSize: 13, fontWeight: !r.isRead ? 700 : 500, color: 'var(--text-primary)' }}>
                        {r.fromEmail}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        {(() => { const d = new Date(r.receivedAt); const h = (Date.now() - d.getTime()) / 3600000; return h < 1 ? `${Math.floor(h * 60)}m ago` : h < 24 ? `${Math.floor(h)}h ago` : d.toLocaleDateString('en', { month: 'short', day: 'numeric' }); })()}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: !r.isRead ? 600 : 400, marginBottom: 2 }}>
                      {r.subject || '(No subject)'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.bodyText?.replace(/<[^>]+>/g, '').slice(0, 80) || '(Empty)'}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Right: reply detail */}
            <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--bg-base)' }}>
              {!selectedReply ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'var(--text-dim)' }}>
                  <Reply size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
                  <p>Select a reply to read</p>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-default)', background: 'var(--bg-surface)' }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                      {selectedReply.subject}
                    </h3>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{selectedReply.fromEmail}</span>
                      <span style={{ margin: '0 6px' }}>→</span>
                      <span>{selectedReply.toEmail}</span>
                      <span style={{ marginLeft: 12, color: 'var(--text-dim)' }}>{new Date(selectedReply.receivedAt).toLocaleString()}</span>
                    </div>
                  </div>
                  {/* Body */}
                  <div style={{ flex: 1, padding: 18, overflowY: 'auto' }}>
                    <div style={{
                      background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                      borderRadius: 10, padding: 16, whiteSpace: 'pre-wrap',
                      fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.7,
                    }}>
                      {selectedReply.bodyText?.replace(/<[^>]+>/g, '') || '(Empty)'}
                    </div>
                    {/* Tip: follow up with official email */}
                    <div style={{
                      marginTop: 12, padding: '10px 14px', borderRadius: 8,
                      background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)',
                      fontSize: 11, color: '#6ee7b7', display: 'flex', gap: 8, alignItems: 'flex-start',
                    }}>
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span><strong>Next step:</strong> This lawyer replied! Follow up from your official email <strong>leo@lawhelper.org</strong> to establish the professional relationship.</span>
                    </div>
                  </div>
                  {/* Quick reply */}
                  <div style={{ padding: 14, borderTop: '1px solid var(--border-default)', display: 'flex', gap: 8, background: 'var(--bg-surface)' }}>
                    <textarea
                      value={replyText} onChange={e => setReplyText(e.target.value)}
                      placeholder="Quick reply from this outlook account..."
                      style={{
                        flex: 1, padding: '8px 12px', fontSize: 12, resize: 'none', height: 60,
                        background: 'var(--bg-input)', color: 'var(--text-primary)',
                        border: '1px solid var(--border-default)', borderRadius: 6, outline: 'none',
                        fontFamily: 'inherit',
                      }}
                    />
                    <button
                      disabled={replySending || !replyText.trim()}
                      onClick={async () => {
                        if (!selectedReply || !replyText.trim()) return;
                        setReplySending(true);
                        try {
                          await fetch(`${API}/emails/reply`, {
                            method: 'POST', headers: authHeaders(),
                            body: JSON.stringify({
                              leadId: selectedReply.leadId,
                              toEmail: selectedReply.fromEmail,
                              fromEmail: selectedReply.toEmail,
                              subject: `Re: ${selectedReply.subject}`,
                              bodyText: replyText,
                              inReplyToMessageId: selectedReply.id,
                            }),
                          });
                          setReplyText('');
                          fetchData();
                        } catch (e) { alert(String(e)); }
                        setReplySending(false);
                      }}
                      className="btn btn-success"
                      style={{ alignSelf: 'flex-end', fontSize: 12 }}
                    >
                      {replySending ? '...' : '📤 Reply'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 14 }}>
          {[
            { Icon: Mail, title: '1. Pick Accounts', desc: 'Unused Outlook from warmup pool', color: '#a5b4fc' },
            { Icon: Users, title: '2. Match Lawyers', desc: '1 account → 1 lawyer', color: '#6ee7b7' },
            { Icon: Send, title: '3. Send Consulting Email', desc: 'Pure text, no links, unique', color: '#fbbf24' },
            { Icon: CheckCircle2, title: '4. Official Follow-up', desc: 'Reply from leo@lawhelper.org', color: '#c4b5fd' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                <span style={{ display: 'inline-flex', padding: 8, borderRadius: 10, background: `${s.color}15` }}>
                  <s.Icon size={20} color={s.color} />
                </span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{s.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
